import { html, nothing } from 'lit';
import { combineHeadingTitle, highlightedHeading } from '../components/highlightedHeading.js';
import {
  applicationPortal,
  type LandingAudienceHeroContent,
  type LandingContent,
  type LandingLocale,
  landingPageContentRepository,
} from '../../shared/content/landingContent.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

type HeroFanKey = 'heroFanApplicant' | 'heroFanCompany';
type HeroFanVariant = 'applicant' | 'company';

const variantByKey: Record<HeroFanKey, HeroFanVariant> = {
  heroFanApplicant: 'applicant',
  heroFanCompany: 'company',
};

const textNode = (
  editor: LandingEditorRenderOptions | undefined,
  elementId: string,
  baseClass: string,
  value: string,
) => html`
  <span
    class=${editorNodeClass(editor, elementId, baseClass)}
    style=${editorNodeStyle(editor, elementId)}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, elementId, event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, elementId, event)}
    >${value}</span
  >
`;

// Small corner arrow that hints the card can be turned (no circle, top-right of each face).
const flipArrow = () => html`
  <span class="vp-hero-fan-card-flip" aria-hidden="true">
    <svg viewBox="0 0 24 24" focusable="false">
      <path d="M7 17 17 7" />
      <path d="M9 7h8v8" />
    </svg>
  </span>
`;

const renderHeroFan = (
  key: HeroFanKey,
  hero: LandingAudienceHeroContent,
  locale: LandingLocale,
  editor?: LandingEditorRenderOptions,
) => {
  if (!hero) return html``; // Failsafe against white screen

  const variant = variantByKey[key];
  // Code defaults are the safety net so the flip side always has complete CTA copy even if a legacy
  // published project never stored it. The editable source of truth stays the (DB) content per card.
  const defaultCards = landingPageContentRepository.getContent(locale)[key]?.cards ?? [];
  // In the editor the section toolbar can reveal every CTA at once so the back copy is editable.
  const flipped = editor?.mode === 'editor' && (editor.flippedFanSections?.has(key) ?? false);

  const iconByVariant = {
    applicant: '',
    company: 'icon-[material-symbols--business-center]',
  };

  return html`
    <section
      class=${editorNodeClass(editor, `section.${key}`, `vp-hero-fan vp-hero-fan--${variant}`)}
      style=${editorNodeStyle(editor, `section.${key}`)}
      @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `section.${key}`, event)}
      @click=${(event: MouseEvent) => editorNodeClick(editor, `section.${key}`, event)}
    >
      <div class="vp-hero-fan-top">
        <div
          class=${editorNodeClass(editor, `${key}.copy`, 'vp-hero-fan-head')}
          style=${editorNodeStyle(editor, `${key}.copy`)}
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.copy`, event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.copy`, event)}
        >
          <span class="vp-hero-fan-badge">
            ${variant === 'applicant' && hero.avatars.length
              ? html`<span class="vp-hero-fan-avatars">
                  ${hero.avatars.slice(0, 3).map(avatar => html`<img src=${avatar} alt="" loading="lazy" />`)}
                </span>`
              : html`<span class="vp-hero-fan-badge-icon ${iconByVariant[variant]}"></span>`}
            ${textNode(editor, `${key}.badge`, 'vp-hero-fan-badge-text', hero.badge)}
          </span>

          ${highlightedHeading({
            title: combineHeadingTitle(hero.title, hero.highlight),
            highlight: hero.highlight,
            className: 'vp-hero-fan-title',
            editor,
            titleElementId: `${key}.title`,
            highlightElementId: `${key}.highlight`,
          })}

          <p
            class=${editorNodeClass(editor, `${key}.intro`, 'vp-hero-fan-intro')}
            style=${editorNodeStyle(editor, `${key}.intro`)}
            @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.intro`, event)}
            @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.intro`, event)}
          >
            ${hero.intro}
          </p>
          <!-- trustLabel element exists in schema but omitted from DOM to achieve clean UI -->
        </div>
      </div>

      <div
        class=${editorNodeClass(editor, `${key}.cards`, 'vp-hero-fan-cards')}
        style=${editorNodeStyle(editor, `${key}.cards`)}
        @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.cards`, event)}
        @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.cards`, event)}
      >
        ${hero.cards.map((card, index) => {
          const ctaText = card.quote ?? defaultCards[index]?.quote ?? '';
          const ctaTitle = card.quoteAuthor ?? defaultCards[index]?.quoteAuthor ?? '';
          const ctaLabel = card.quoteRole ?? defaultCards[index]?.quoteRole ?? '';
          const hasCta = Boolean(ctaTitle || ctaText || ctaLabel);
          const cardClass = `${editorNodeClass(editor, `${key}.card.${index}`, `vp-hero-fan-card vp-hero-fan-card--${index}`)}${
            flipped ? ' is-flipped' : ''
          }`;
          return html`
            <a
              class=${cardClass}
              style=${editorNodeStyle(editor, `${key}.card.${index}`)}
              href=${card.href}
              aria-label=${hasCta && ctaLabel ? `${card.title}: ${ctaLabel}` : card.title}
              @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.card.${index}`, event)}
              @click=${(event: MouseEvent) => {
                if (editor?.mode === 'editor') {
                  event.preventDefault();
                  editorNodeClick(editor, `${key}.card.${index}`, event);
                  return;
                }
                // First activation reveals the CTA; activating the revealed side follows its link.
                if (!hasCta) return;
                const cardEl = event.currentTarget as HTMLElement;
                if (cardEl.classList.contains('is-flipped')) return;
                event.preventDefault();
                const willFlip = !cardEl.classList.contains('is-flipped');
                // Only one CTA is open at a time: flip any sibling card back first.
                cardEl
                  .closest('.vp-hero-fan-cards')
                  ?.querySelectorAll<HTMLElement>('.vp-hero-fan-card.is-flipped')
                  .forEach(other => {
                    if (other === cardEl) return;
                    other.classList.remove('is-flipped');
                    other.setAttribute('aria-expanded', 'false');
                  });
                cardEl.classList.toggle('is-flipped', willFlip);
                cardEl.setAttribute('aria-expanded', String(willFlip));
              }}
            >
              <div class="vp-hero-fan-card-inner">
                <div class="vp-hero-fan-card-face vp-hero-fan-card-face--front">
                  <img
                    class=${editorNodeClass(editor, `${key}.card.${index}.image`, 'vp-hero-fan-card-image')}
                    style=${editorNodeStyle(editor, `${key}.card.${index}.image`)}
                    src=${card.image}
                    alt=${card.imageAlt}
                    loading="lazy"
                    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.card.${index}.image`, event)}
                    @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.card.${index}.image`, event)}
                  />
                  <div class="vp-hero-fan-card-top">
                    ${textNode(editor, `${key}.card.${index}.eyebrow`, 'vp-hero-fan-card-eyebrow', card.eyebrow)}
                    ${textNode(editor, `${key}.card.${index}.title`, 'vp-hero-fan-card-title', card.title)}
                  </div>
                  ${flipArrow()}
                </div>
                ${hasCta
                  ? html`
                      <div class="vp-hero-fan-card-face vp-hero-fan-card-face--back">
                        <span class="vp-hero-fan-cta-icon icon-[material-symbols--arrow-outward-rounded]" aria-hidden="true"></span>
                        <h3
                          class=${editorNodeClass(editor, `${key}.card.${index}.quoteAuthor`, 'vp-hero-fan-cta-title')}
                          style=${editorNodeStyle(editor, `${key}.card.${index}.quoteAuthor`)}
                          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.card.${index}.quoteAuthor`, event)}
                          @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.card.${index}.quoteAuthor`, event)}
                        >${ctaTitle}</h3>
                        <p
                          class=${editorNodeClass(editor, `${key}.card.${index}.quote`, 'vp-hero-fan-cta-text')}
                          style=${editorNodeStyle(editor, `${key}.card.${index}.quote`)}
                          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `${key}.card.${index}.quote`, event)}
                          @click=${(event: MouseEvent) => editorNodeClick(editor, `${key}.card.${index}.quote`, event)}
                          >${ctaText}</p
                        >
                        ${ctaLabel
                          ? html`<span class="vp-hero-fan-cta-link">
                              ${textNode(editor, `${key}.card.${index}.quoteRole`, 'vp-hero-fan-cta-label', ctaLabel)}
                              <span class="icon-[material-symbols--arrow-forward-rounded]" aria-hidden="true"></span>
                            </span>`
                          : ''}
                        ${flipArrow()}
                      </div>
                    `
                  : ''}
              </div>
            </a>
          `;
        })}
      </div>

      ${variant === 'applicant'
        ? html`<div class="vp-hero-fan-cta">
            <a
              class="vp-action vp-action-primary vp-hero-fan-cta-button"
              href=${applicationPortal.overview}
              @click=${(event: MouseEvent) => {
                if (editor?.mode === 'editor') event.preventDefault();
              }}
            >
              ${locale === 'tr' ? 'Hemen başvur' : locale === 'en' ? 'Apply now' : 'Jetzt anmelden'}
              <span class="icon-[material-symbols--arrow-forward-rounded]" aria-hidden="true"></span>
            </a>
          </div>`
        : nothing}
    </section>
  `;
};

export const heroFanSection = (key: HeroFanKey, content: LandingContent, editor?: LandingEditorRenderOptions) =>
  renderHeroFan(key, content[key], content.locale, editor);
